<script setup lang="ts">
/**
 * Sidebar droite de la page boutique (pattern BslugSidebar, sticky) :
 *  - card contact de l'association
 *  - card récap commande (visible dès qu'il y a des articles)
 *  - card branding Donoor (panier vide)
 */
import { fraisDesParts, type PartDuPanier } from "~/utils/plusieursFois";
import PublicPageTotauxPlusieursFois from "~/components/public-page/PublicPageTotauxPlusieursFois.vue";
import type {
  Shop,
  ShopCartLineView,
  ShopProduct,
  ShopProductVariant,
  ShopPromoApplied,
} from "~/types/shop";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  shop: Shop;
  checkoutStep: number;
  cartLines: ShopCartLineView[];
  totalItems: number;
  subtotal: number;
  promoApplied: ShopPromoApplied | null;
  promoCode: string;
  promoLoading: boolean;
  promoError: string;
  promoDiscount: number;
  donationAmount: number;
  /** Soutien Donoor (pourboire) en EUROS — payé en plus par l'acheteur. */
  donoorTipAmount?: number;
  /** Frais de port appliqués en EUROS (0 = retrait, franco ou aucun mode). */
  shippingFee: number;
  /** Nom du mode choisi — affiché en libellé de la ligne « frais de port ». */
  shippingMethodName: string | null;
  feesAmount: number;
  includeFees: boolean;
  /** Parts du paiement en plusieurs fois choisi (vide : en une fois). */
  partsPlusieursFois?: PartDuPanier[];
  /** Vitrine : affiche « Commander » sous le total (le tunnel a sa propre action). */
  showCheckoutAction?: boolean;
  /** Achat possible (fenêtre de vente ouverte ET paiement configuré). */
  canBuy?: boolean;
  /**
   * Sur MOBILE, place cette colonne AVANT le contenu principal — opt-in.
   * Utilisé par le TUNNEL (`/panier`) : la colonne y porte seule le détail des
   * articles, elle doit donc précéder le bouton « Payer », sinon l'acheteur
   * valide sans avoir vu ce qu'il paie. La VITRINE ne l'active pas : le
   * catalogue doit y rester la première chose visible.
   */
  mobileFirst?: boolean;
  /**
   * Rend les lignes MODIFIABLES (quantité, suppression) — opt-in.
   * Activé par le tunnel tant que le paiement n'est pas engagé : le bloc
   * « Votre panier » ayant disparu, c'est le dernier endroit où corriger sa
   * commande. Volontairement FAUX au step paiement — le montant Stripe y est
   * déjà fixé, changer une quantité rendrait l'écran incohérent.
   */
  editableLines?: boolean;
  /**
   * Plafond d'une ligne (stock restant + max_per_order), fourni par la page.
   * Sans lui le bouton « + » ne se désactive jamais et l'acheteur peut
   * dépasser le stock ; on retombe alors sur le refus serveur à la commande.
   */
  getMaxForLine?: (
    product: ShopProduct,
    variant: ShopProductVariant | null,
    excludeUid?: string,
  ) => number;
}>();

defineEmits<{
  (e: "update:promo-code" | "remove-line", v: string): void;
  (e: "apply-promo" | "remove-promo" | "checkout"): void;
  (e: "update-quantity", uid: string, quantity: number): void;
}>();

/** Plafond de la ligne — `Infinity` si la page ne fournit pas le calcul. */
function lineMax(line: ShopCartLineView): number {
  if (!props.getMaxForLine) return Infinity;
  return props.getMaxForLine(line.product, line.variant, line.uid);
}

const contactEmail = computed(() => props.shop.settings?.contact_email || null);

const config = useRuntimeConfig();
const assetsBaseUrl = computed(
  () => (config.public.apiUrl as string) || "https://api.donoor.org",
);

/**
 * Vignette du produit — même construction d'URL que `BoutiqueCartLines`
 * (mêmes assets Directus, donc même entrée de cache navigateur : la photo est
 * déjà chargée quand l'acheteur arrive ici depuis le panier).
 */
function thumb(line: ShopCartLineView): string | null {
  const id = line.product.images?.[0];
  if (!id) return null;
  return `${assetsBaseUrl.value}/api/assets/${id}?width=120&height=120&fit=cover&format=webp&quality=80`;
}

const totalAfterDiscount = computed(() =>
  Math.max(0, props.subtotal - props.promoDiscount),
);
// Total = marchandise nette + PORT + don + SOUTIEN DONOOR (+ frais bancaires si
// couverts) — miroir exact du serveur (calculateFeesForShop). Le soutien y
// figure car il est débité à l'acheteur, même s'il ne revient pas à l'asso :
// l'omettre afficherait ici un total inférieur au montant réellement payé.
const grandTotal = computed(
  () =>
    totalAfterDiscount.value +
    props.shippingFee +
    props.donationAmount +
    (props.donoorTipAmount ?? 0) +
    (props.includeFees ? props.feesAmount : 0),
);

// En plusieurs fois : frais de TOUTE la commande (un frais fixe par
// paiement) et bas du détail = total réel, aujourd'hui, puis la suite.
const enPlusieursFois = computed(() => (props.partsPlusieursFois?.length ?? 0) >= 2);
const fraisAffiches = computed(() =>
  enPlusieursFois.value
    ? fraisDesParts(
        props.partsPlusieursFois ?? [],
        totalAfterDiscount.value + props.shippingFee + props.donationAmount + (props.donoorTipAmount ?? 0),
      )
    : props.feesAmount,
);
</script>

<template>
  <div
    class="lg:order-none lg:w-[320px] shrink-0 mt-0 md:mt-4 lg:mt-6 pb-6"
    :class="mobileFirst ? 'order-first' : ''"
  >
    <div class="lg:sticky lg:top-6 space-y-4 md:space-y-6">
      <!--
        Tête de colonne : vendeur + Partager + Panier (Karim 2026-08-09).
        Ils vivaient dans l'en-tête de la colonne principale, à gauche ; ils
        appartiennent à la colonne de droite, celle qui porte « Détails de
        votre commande ». Fourni par la page via un slot, pour ne pas
        dupliquer le bouton panier ni son état.
      -->
      <slot name="head" />

      <!-- Contact association -->
      <div v-if="contactEmail" class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-5">
        <p class="text-xs text-[#717198] dark:text-gray-500 mb-1">{{ $t("boutique.sidebar.uneQuestionProduit") }}</p>
        <p class="text-xs text-[#717198] dark:text-gray-500">{{ $t("boutique.commun.contacterAssociation") }}</p>
        <a :href="`mailto:${contactEmail}`" class="block mt-2 text-sm font-medium text-[#212121] dark:text-white hover:underline">{{ contactEmail }}</a>
      </div>

      <!-- Récap commande — VISIBLE À TOUTES LES LARGEURS.
           Il était `hidden lg:block` tant que le corps de page portait son
           propre bloc « Votre commande » ; celui-ci ayant été retiré comme
           doublon (Karim 2026-08-09), le masquer sous `lg` priverait
           l'acheteur mobile du détail de ce qu'il paie — c'est-à-dire la
           majorité du trafic. Sticky uniquement sur desktop. -->
      <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 translate-y-2" leave-active-class="transition-all duration-200 ease-in" leave-to-class="opacity-0 -translate-y-2">
        <div v-if="totalItems > 0" class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] overflow-hidden">
          <div class="p-5">
            <h3 class="text-base font-bold text-[#2d2e5c] dark:text-white mb-4">{{ $t("boutique.sidebar.detailsCommande") }}</h3>

            <!--
              Une VIGNETTE par ligne (Karim 2026-08-09) : cette colonne est
              désormais le seul récapitulatif à l'étape paiement — le bloc
              « Votre commande » du corps a été retiré comme redondant. Une
              liste purement textuelle y laissait l'acheteur vérifier de
              mémoire qu'il paie le bon article ; la photo lève le doute d'un
              coup d'œil, surtout entre deux variantes du même produit
              (T-shirt L·Blanc vs M·Noir).
            -->
            <div class="space-y-3">
              <div v-for="line in cartLines" :key="line.uid" class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-lg overflow-hidden bg-gray-100 dark:bg-[#1a1a1a] shrink-0">
                  <img
                    v-if="thumb(line)"
                    :src="thumb(line)!"
                    :alt="line.product.name"
                    loading="lazy"
                    class="w-full h-full object-cover"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center">
                    <UIcon name="i-lucide-package" class="size-3.5 text-gray-300 dark:text-gray-600" />
                  </div>
                </div>

                <!--
                  Variante et quantité sur une DEUXIÈME LIGNE (Karim
                  2026-08-09). Sur une seule ligne, dans une colonne de 320 px,
                  « T-shirt Donoor (L · Blanc) x1 » débordait et se faisait
                  tronquer en « … » : l'acheteur perdait justement la variante,
                  seule chose qui distingue deux lignes du même produit. Le nom
                  garde `truncate` (il peut être long) ; la variante, elle, est
                  toujours affichée en entier.
                -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm text-[#2d2e5c] dark:text-white truncate">
                    {{ line.product.name }}
                  </p>
                  <p class="text-xs text-[#717198] dark:text-gray-400">
                    <span v-if="line.variantLabel">{{ line.variantLabel }}</span>
                    <span v-if="line.variantLabel && !editableLines"> · </span>
                    <span v-if="!editableLines">x{{ line.quantity }}</span>
                  </p>

                  <!--
                    QUANTITÉ MODIFIABLE ici (Karim 2026-08-09) : cette colonne
                    ayant remplacé le bloc « Votre panier », c'est le seul
                    endroit où l'acheteur peut encore corriger sa commande
                    avant de payer. Mêmes évènements que `BoutiqueCartLines`,
                    donc mêmes garde-fous de stock côté page.
                  -->
                  <div v-if="editableLines" class="flex items-center gap-1 mt-1.5">
                    <button
                      type="button"
                      :aria-label="$t('boutique.cartLines.ariaDiminuer', { nom: line.product.name })"
                      class="w-6 h-6 rounded-md border border-gray-200 dark:border-[#333] flex items-center justify-center text-[#2d2e5c] dark:text-white transition-colors hover:bg-gray-100 dark:hover:bg-[#222]"
                      @click="$emit('update-quantity', line.uid, line.quantity - 1)"
                    >
                      <UIcon name="i-lucide-minus" class="size-3" />
                    </button>
                    <span class="w-5 text-center text-xs font-semibold text-[#2d2e5c] dark:text-white tabular-nums">
                      {{ line.quantity }}
                    </span>
                    <button
                      type="button"
                      :aria-label="$t('boutique.cartLines.ariaAugmenter', { nom: line.product.name })"
                      class="w-6 h-6 rounded-md border border-gray-200 dark:border-[#333] flex items-center justify-center text-[#2d2e5c] dark:text-white disabled:opacity-30 transition-colors hover:bg-gray-100 dark:hover:bg-[#222]"
                      :disabled="line.quantity >= lineMax(line)"
                      @click="$emit('update-quantity', line.uid, line.quantity + 1)"
                    >
                      <UIcon name="i-lucide-plus" class="size-3" />
                    </button>
                    <button
                      type="button"
                      :aria-label="$t('boutique.cartLines.ariaRetirer', { nom: line.product.name })"
                      class="w-6 h-6 rounded-md flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-0.5"
                      @click="$emit('remove-line', line.uid)"
                    >
                      <UIcon name="i-lucide-trash-2" class="size-3" />
                    </button>
                  </div>
                </div>

                <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white shrink-0 ml-3">
                  {{ line.lineTotal > 0 ? formatCurrency(line.lineTotal) : $t("boutique.commun.gratuit") }}
                </span>
              </div>
            </div>

            <!-- Promo UNIQUEMENT au step récap (3) — progression UX claire -->
            <div v-if="checkoutStep >= 3 && subtotal > 0" class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <PublicPagePromoCodeInput
                variant="b"
                :model-value="promoCode"
                :applied="promoApplied"
                :loading="promoLoading"
                :error="promoError"
                @update:model-value="$emit('update:promo-code', $event)"
                @apply="$emit('apply-promo')"
                @remove="$emit('remove-promo')"
              />
            </div>

            <div v-if="checkoutStep >= 3 && promoDiscount > 0" class="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <span class="text-sm text-emerald-600">{{ $t("boutique.commun.reduction") }}</span>
              <span class="text-sm font-semibold text-emerald-600">-{{ formatCurrency(promoDiscount) }}</span>
            </div>
          </div>

          <div v-if="checkoutStep >= 3" class="px-5 pb-2 space-y-1.5">
            <!-- « Articles » = marchandise après remise. Cette ligne manquait :
                 le détail commençait au port, si bien que la somme affichée ne
                 retombait pas sur le total (Karim 2026-08-10). -->
            <div class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t("boutique.sidebar.articlesLabel") }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(totalAfterDiscount) }}</span>
            </div>
            <div v-if="shippingMethodName" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ shippingMethodName }}</p>
              <span class="text-xs text-[#717198]">
                {{ shippingFee > 0 ? formatCurrency(shippingFee) : $t("boutique.commun.offert") }}
              </span>
            </div>
            <div v-if="donationAmount > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t("boutique.commun.donA", { association: shop.organization_name || $t('boutique.commun.associationGenerique') }) }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(donationAmount) }}</span>
            </div>
            <div v-if="(donoorTipAmount ?? 0) > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t("boutique.sidebar.soutienDonoor") }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(donoorTipAmount ?? 0) }}</span>
            </div>
            <div v-if="includeFees && fraisAffiches > 0" class="flex items-center justify-between">
              <p class="text-xs text-[#717198]">{{ $t("boutique.sidebar.fraisTransaction") }}</p>
              <span class="text-xs text-[#717198]">{{ formatCurrency(fraisAffiches) }}</span>
            </div>
          </div>

          <div class="border-t border-gray-100 px-5 py-4">
            <PublicPageTotauxPlusieursFois v-if="checkoutStep >= 3 && enPlusieursFois" :parts="partsPlusieursFois ?? []" />
            <div v-else class="flex items-center justify-between">
              <span class="text-base font-bold text-[#2d2e5c]">{{ checkoutStep >= 3 ? $t("boutique.sidebar.totalAPayer") : $t("boutique.cartDrawer.sousTotal") }}</span>
              <span class="text-lg font-bold" :style="{ color: 'var(--accent, #2d2e5c)' }">
                {{ checkoutStep >= 3
                  ? (grandTotal > 0 ? formatCurrency(grandTotal) : $t("boutique.commun.gratuit"))
                  : (subtotal > 0 ? formatCurrency(subtotal) : $t("boutique.commun.gratuit")) }}
              </span>
            </div>

            <!--
              Commander DEPUIS le récapitulatif (Karim 2026-08-09) : le rappel
              panier du bas de catalogue a été retiré (il doublonnait cette
              colonne), l'action doit donc vivre où l'acheteur lit son total.
              Affiché sur la VITRINE seulement (`checkoutStep === 1`) : dans le
              tunnel, l'action est déjà portée par l'étape courante.
            -->
            <!-- Panier + Commander côte à côte : l'un ouvre le détail, l'autre
                 engage la commande — les deux actions du même total. -->
            <div v-if="checkoutStep === 1 && showCheckoutAction" class="mt-3 flex items-center gap-2">
              <slot name="cart" />
            </div>
            <button
              v-if="checkoutStep === 1 && showCheckoutAction"
              type="button"
              data-testid="btn-sidebar-checkout"
              class="w-full mt-3 min-h-11 rounded-2xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed"
              :class="canBuy
                ? 'bg-black text-white dark:bg-white dark:text-black'
                : 'bg-gray-200 text-gray-500 dark:bg-[#262626] dark:text-gray-500'"
              :disabled="!canBuy"
              @click="$emit('checkout')"
            >
              {{ $t("boutique.commun.commander") }}
            </button>
          </div>
        </div>
      </Transition>

      <!--
        « Pourquoi soutenir Donoor ? » RETIRÉ de la boutique (Karim
        2026-08-09) : sur une vitrine, la colonne de droite sert à voir son
        panier et à commander — un argumentaire sur la plateforme y occupe la
        place sans aider à acheter. Le composant reste utilisé tel quel par la
        billetterie (BslugSidebar) et les adhésions (AslugSidebar), où il est
        conservé : ne pas le supprimer.
      -->
      <!-- <PublicPageDonoorBrandingCard
        v-if="totalItems === 0"
        :organization-name="shop.organization_name"
        action="commande"
      /> -->
    </div>
  </div>
</template>
